import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, ArrowLeft, ShieldCheck, Zap, Plus, Minus } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { ProductSEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const { t } = useLanguage();
  const { addItem, items } = useCart();
  const { toggleItem, isInWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/products/${id}`);
        setProduct(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 animate-pulse">
          <div className="aspect-[3/4] bg-[#F4F0EB]" />
          <div className="space-y-4 py-8">
            <div className="h-3 bg-[#F4F0EB] w-24" />
            <div className="h-8 bg-[#F4F0EB] w-48" />
            <div className="h-4 bg-[#F4F0EB] w-20" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-24 text-center">
        <p className="text-sm text-[#9CA3AF]">{t("common.error")}</p>
      </div>
    );
  }

  const wishlisted = isInWishlist(product.id);

  return (
    <div className="px-6 md:px-12 lg:px-24 py-8 md:py-16" data-testid="product-detail-page">
      <ProductSEO product={product} />
      {/* Back */}
      <Link
        to="/shop"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors mb-8"
        data-testid="back-to-shop"
      >
        <ArrowLeft size={14} strokeWidth={1.5} />
        {t("common.back")}
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
        {/* Image Gallery */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="aspect-[3/4] bg-[#F4F0EB] overflow-hidden mb-3">
            <img
              src={product.images?.[activeImage] || product.images?.[0] || "https://via.placeholder.com/600x800"}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-image"
            />
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="image-thumbnails">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`w-16 h-20 md:w-20 md:h-24 flex-shrink-0 bg-[#F4F0EB] overflow-hidden transition-all ${
                    activeImage === i
                      ? "ring-2 ring-[#1A1A1A] ring-offset-1"
                      : "opacity-60 hover:opacity-100"
                  }`}
                  data-testid={`thumbnail-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Details */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-col justify-center py-4"
        >
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">
            {product.brand}
          </p>
          <h1
            className="text-3xl md:text-4xl mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="product-name"
          >
            {product.name}
          </h1>
          <div className="flex items-center gap-3 mb-8">
            {product.discount > 0 && (
              <p className="text-lg tracking-wider line-through text-[#9CA3AF]">
                &euro;{product.price?.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
              </p>
            )}
            <p className={`text-lg tracking-wider ${product.discount > 0 ? "text-[#8B2020] font-semibold" : ""}`} data-testid="product-price">
              &euro;{(product.discount > 0 ? product.price * (1 - product.discount / 100) : product.price)?.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
            </p>
            {product.discount > 0 && (
              <span className="bg-[#8B2020] text-white text-[10px] tracking-wider font-semibold px-2 py-0.5" data-testid="product-discount-badge">
                -{product.discount}%
              </span>
            )}
          </div>

          <p className="text-sm text-[#1A1A1A]/70 leading-relaxed mb-8 tracking-wide whitespace-pre-line" data-testid="product-description">
            {product.description}
          </p>

          <div className="flex items-center gap-2 mb-8">
            <span
              className={`text-[10px] uppercase tracking-[0.15em] ${
                product.stock_quantity > 0 ? "text-green-700" : "text-red-500"
              }`}
              data-testid="product-stock-status"
            >
              {product.stock_quantity > 0
                ? `${t("product.in_stock")} (${product.stock_quantity})`
                : t("product.out_of_stock")}
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-4 mb-10">
            <button
              onClick={() => {
                const inCart = items.find((i) => i.product.id === product.id);
                const currentQty = inCart ? inCart.quantity : 0;
                if (currentQty >= (product.stock_quantity || 0)) {
                  toast.error(t("product.max_stock"));
                  return;
                }
                addItem(product);
                toast.success(product.name + " added to bag");
              }}
              disabled={!product.stock_quantity || product.stock_quantity <= 0}
              className="btn-luxury flex-1 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="add-to-cart-btn"
            >
              {t("product.add_cart")}
            </button>
            <button
              onClick={() => toggleItem(product)}
              className={`w-14 h-14 flex items-center justify-center border transition-all ${
                wishlisted
                  ? "bg-[#1A1A1A] border-[#1A1A1A] text-[#FDFBF7]"
                  : "bg-transparent border-[#E6E2DD] text-[#1A1A1A] hover:border-[#1A1A1A]"
              }`}
              data-testid="wishlist-btn"
            >
              <Heart size={18} strokeWidth={1.5} className={wishlisted ? "fill-current" : ""} />
            </button>
          </div>

          {/* Info */}
          <div className="space-y-4 pt-8 border-t border-[#E6E2DD]">
            <div className="flex items-center gap-3">
              <ShieldCheck size={16} strokeWidth={1.5} className="text-[#9CA3AF]" />
              <span className="text-xs tracking-wide text-[#9CA3AF]">{t("product.authenticity")}</span>
            </div>
            <div className="flex items-center gap-3">
              <Zap size={16} strokeWidth={1.5} className="text-[#9CA3AF]" />
              <span className="text-xs tracking-wide text-[#9CA3AF]">{t("product.fast_delivery")}</span>
            </div>
          </div>

          <p className="mt-4 text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">
            {t("shop.category")}: {product.category}
          </p>

          {/* Expandable Info Sections */}
          <div className="mt-10 border-t border-[#E6E2DD]" data-testid="product-info-sections">
            <InfoSection
              title={t("product.further_info")}
              content={t("product.further_info_content")}
              testId="further-info"
            />
            <InfoSection
              title={t("product.returns_shipping")}
              content={t("product.returns_shipping_content")}
              testId="returns-shipping"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function InfoSection({ title, content, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E6E2DD]" data-testid={`info-section-${testId}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
        aria-expanded={open}
        data-testid={`info-toggle-${testId}`}
      >
        <span className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A] group-hover:opacity-70 transition-opacity">
          {title}
        </span>
        {open ? (
          <Minus size={16} strokeWidth={1.25} className="text-[#1A1A1A] flex-shrink-0" />
        ) : (
          <Plus size={16} strokeWidth={1.25} className="text-[#1A1A1A] flex-shrink-0" />
        )}
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <p
          className="text-xs md:text-sm text-[#1A1A1A]/70 leading-relaxed tracking-wide whitespace-pre-line pb-6 pr-4"
          data-testid={`info-content-${testId}`}
        >
          {content}
        </p>
      </motion.div>
    </div>
  );
}
